import { CommonModule } from '@angular/common';
import { Component, inject, Input, NgModule } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatToolbarModule } from '@angular/material/toolbar';
import { ActivatedRoute, ActivatedRouteSnapshot, ActivationEnd, Router } from '@angular/router';
import { DataTerminalSessionService } from '@data-terminal/data-access/session-service';
import { UserMachinesService } from '@data-terminal/data-access/user-machines';
import { FeatureDashboardDataAccessModule } from '@data-terminal/feature-dashboards-data-access/feature-dashboard-data-access.module';
import { FooterLeaveDialogComponent } from '@data-terminal/feature-dialogs/dialogs';
import { ManageAssistantsDialogData } from '@data-terminal/feature-dialogs/dialogs/manage-assistants-dialog/manage-assistants-dialog.component';
import { ManageAssistantsDialogService } from '@data-terminal/feature-dialogs/services/manage-assistants-dialog/manage-assistants-dialog.service';
import { AbstractSignOffProcessService } from '@data-terminal/feature-dialogs/services/processes/abstract-sign-off-process.service';
import { EndShiftProcessService } from '@data-terminal/feature-dialogs/services/processes/end-shift-process.service';
import { SignOffProcessService } from '@data-terminal/feature-dialogs/services/processes/sign-off-process.service';
import { InactivityTrackerService } from '@data-terminal/feature-inactivity-tracker/services';
import { FooterDialogsActions } from '@data-terminal/shared-models/dialogs/dialogs';
import { AvailableAssistants, Machine } from '@data-terminal/shared-models/machine';
import { Role } from '@data-terminal/shared-models/role';
import { User } from '@data-terminal/shared-models/session';
import { Required } from '@data-terminal/utils/decorators';
import { TimeFormatterPipe, TimeFormatterPipeModule } from '@data-terminal/utils/pipes';
import { HplusRedirectionService, LoadingStateService } from '@data-terminal/utils/services';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateModule } from '@ngx-translate/core';
import { DateTime } from 'luxon';
import { filter, interval, map, Observable, take, takeWhile, timer } from 'rxjs';

@UntilDestroy()
@Component({
    providers: [TimeFormatterPipe],
    selector: 'app-footer',
    templateUrl: './footer.component.html',
    styleUrls: ['./footer.component.scss'],
    standalone: false,
})
export class FooterComponent {
    private readonly dialog = inject(MatDialog);
    private readonly session = inject(DataTerminalSessionService);
    private readonly signOffProcess = inject(SignOffProcessService);
    private readonly endShiftProcess = inject(EndShiftProcessService);
    private readonly loadingStateService = inject(LoadingStateService);
    private readonly hplusRedirectionService = inject(HplusRedirectionService);
    private readonly inactivityTrackerService = inject(InactivityTrackerService);
    private readonly userMachinesService = inject(UserMachinesService);
    private readonly router = inject(Router);
    private readonly manageAssistantsDialogService = inject(ManageAssistantsDialogService);

    @Input()
    @Required()
    activatedRoute?: ActivatedRoute;

    @Input()
    availableAssistants: AvailableAssistants[] = [];

    @Input()
    isTablet = false;

    @Input()
    isContinuousScanningEnabled = false;

    public inactiveWorkstations$ = timer(0, 1000).pipe(
        map(() => this.inactivityTrackerService.getInactiveWorkstations())
    );

    public user!: User;
    public machineId = '';
    public machineSignInTimestamp: number | undefined;
    public machineSignInTimer$!: Observable<number | undefined>;
    public inactivityTimer$: Observable<number> | undefined;
    public displayFooterTime = false;
    public allMachines$: Observable<Machine[]>;
    public assignedMachines$: Observable<Machine[]>;

    constructor() {
        this.allMachines$ = this.userMachinesService.getAllMachines$();
        this.assignedMachines$ = this.userMachinesService.getAssignedMachines$();
        this.loadingStateService.isProfileLoading$.pipe(take(2)).subscribe(() => {
            this.user = this.session.getCurrentDataTerminalUser();
        });
        this.router.events
            .pipe(
                filter((event) => {
                    return event instanceof ActivationEnd;
                })
            )
            .subscribe((route) => {
                this.setDataFromRoute((route as ActivationEnd).snapshot);
            });
        this.setDataFromRoute(this.router.routerState.snapshot.root);

        this.inactiveWorkstations$.pipe(untilDestroyed(this)).subscribe(() => {
            this.getFooterInactivityData();
        });
    }

    private setDataFromRoute(route: ActivatedRouteSnapshot): void {
        this.machineId = this.getMachineId(route || this.machineId);
        this.machineSignInTimestamp = this.getMachineSignInTimestamp(route);
        this.machineSignInTimer$ = interval(1000).pipe(
            takeWhile(() => !!this.machineSignInTimestamp, !!this.machineId),
            map(() =>
                this.machineSignInTimestamp
                    ? DateTime.now().diff(DateTime.fromMillis(this.machineSignInTimestamp)).milliseconds
                    : 0
            )
        );
    }

    private getFooterInactivityData(): void {
        this.inactivityTimer$ = this.inactivityTrackerService.getInactiveWorkstationsTimersMap().get(this.machineId);
        this.displayFooterTime = this.inactivityTrackerService.getInactiveWorkstationsTimersMap().has(this.machineId);
    }

    private getMachineId(route: ActivatedRouteSnapshot): string {
        if (route.firstChild === undefined) {
            return '';
        }
        if (route.firstChild === null) {
            return route?.params?.machineId;
        }

        return this.getMachineId(route.firstChild);
    }

    private getMachineSignInTimestamp(route: ActivatedRouteSnapshot): number | undefined {
        if (route.firstChild === undefined) {
            return undefined;
        }

        if (route.firstChild === null) {
            return route?.data?.machineSignInTimestamp;
        }

        return this.getMachineSignInTimestamp(route.firstChild);
    }

    public onLeaveButtonClick(): void {
        const hasAssignedMachines = this.userMachinesService.getAssignedMachines().length > 0;

        if (hasAssignedMachines) {
            const config = new MatDialogConfig();
            config.width = '99vw';
            config.maxWidth = '99vw';
            config.height = '99vh';
            config.maxHeight = '99vh';

            const dialog = this.dialog.open(FooterLeaveDialogComponent, config);

            dialog
                .beforeClosed()
                .pipe(filter((data: FooterDialogsActions | false) => data !== false))
                .subscribe((data) => {
                    switch (data) {
                        case FooterDialogsActions.SIGN_OFF_WORKSTATION: {
                            this.startSignOffProcess();
                            break;
                        }
                        case FooterDialogsActions.END_SHIFT: {
                            this.startEndShiftProcess();
                            break;
                        }
                        case FooterDialogsActions.CLOSE_APPLICATION: {
                            this.hplusRedirectionService.redirect();
                            break;
                        }
                        case FooterDialogsActions.MANAGE_ASSISTANTS: {
                            this.startAssistantsManagementProcess();
                            break;
                        }
                        default:
                            break;
                    }
                });
        } else {
            this.hplusRedirectionService.redirect();
        }
    }

    private startSignOffProcess(): void {
        this.startGenericProcess(this.signOffProcess, 'DC.SIGN_OFF_DIALOG');
    }

    private startEndShiftProcess(): void {
        this.startGenericProcess(this.endShiftProcess, 'DC.END_SHIFT_DIALOG', true);
    }

    private startGenericProcess(
        process: AbstractSignOffProcessService,
        i18nPrefix: string,
        endApplication = false
    ): void {
        this.userMachinesService.startSignOffProcess();
        process.startProcess(this.user, i18nPrefix, endApplication).subscribe();
    }

    private startAssistantsManagementProcess(): void {
        const data: ManageAssistantsDialogData = {
            workstations: this.userMachinesService
                .getAllMachines()
                .filter((machine) =>
                    machine.signedOnUsers.some(
                        (user) => user.userId === this.user.userId && (user.role === Role.OPERATOR || user.captain)
                    )
                ),
            userId: this.user.userId,
            availableAssistants: this.availableAssistants,
        };

        this.manageAssistantsDialogService.openDialog(data);
    }
}

@NgModule({
    declarations: [FooterComponent],
    exports: [FooterComponent],
    imports: [
        MatIconModule,
        MatDividerModule,
        MatToolbarModule,
        MatButtonModule,
        TranslateModule,
        CommonModule,
        FeatureDashboardDataAccessModule,
        TimeFormatterPipeModule,
    ],
})
export class FooterComponentModule {}
